import { useAtom } from "jotai";
import debounce from "lodash.debounce";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { currentStep } from "@/app/store";

import { useCrefazClt } from "@/app/contexts/CrefazCltContext";
import { useCrefazCltAuthorization } from "@/app/hooks/useCrefazCltAuthorization";
import { AuthorizationStatusEnum } from "@/app/services/authorizationService/getAuthorization";
import { crefazCltService } from "@/app/services/crefazCltService";
import {
  PrazoValor,
  SimulationResponse,
} from "@/app/services/crefazCltService/postSimulation";
import { useToast } from "@/view/components/ui/use-toast";
import { useRegisterSupportContext } from "@/app/store/supportScreenContext";
import { PartnerType } from "@/app/utils/partners.enum";

const RECALC_DELAY = 1500;

export default function useCrefazCltSimulation() {
  const { preTransactionId } = useParams();
  const { toast } = useToast();
  const {
    customerData,
    setCustomerData,
    clearCustomerData,
    preTransaction,
    setPreTransaction,
    setSelectedOffer,
  } = useCrefazClt();
  const [, setStep] = useAtom(currentStep);
  const [isProposalModalOpen, setIsProposalModalOpen] = useState(false);

  const authorization = useCrefazCltAuthorization(preTransactionId, {
    initialStatus:
      preTransaction?.id === preTransactionId
        ? preTransaction?.status
        : undefined,
    initialSmsSent:
      preTransaction?.id === preTransactionId
        ? preTransaction?.autorizacaoEnviada
        : undefined,
  });

  const isAuthorized =
    authorization.status === AuthorizationStatusEnum.AUTHORIZED;

  const authorizedCustomer = authorization.customer;

  useEffect(() => {
    if (
      !authorizedCustomer?.cpf ||
      customerData?.cpf === authorizedCustomer.cpf
    )
      return;

    clearCustomerData();
    setCustomerData({
      cpf: authorizedCustomer.cpf,
      name: authorizedCustomer.name,
    });
  }, [authorizedCustomer, customerData?.cpf, setCustomerData]);

  const [simulation, setSimulation] = useState<SimulationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [amountInput, setAmountInput] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isConfirming, setIsConfirming] = useState(false);

  const runSimulation = async (
    payload: { valor?: number; vencimento?: string } = {},
    isRecalc = false,
  ) => {
    if (!preTransactionId) return;

    try {
      if (isRecalc) {
        setIsRecalculating(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await crefazCltService.postSimulation(
        preTransactionId,
        payload,
      );

      setSimulation(response);
      setSelectedIndex(0);
      setDueDate(response.vencimento);

      if (!isRecalc) {
        setAmountInput(formatAmount(response.valorSolicitado));
      }
    } catch (err: any) {
      setError(err?.message || "Não foi possível simular o crédito");
    } finally {
      setIsRecalculating(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthorized || simulation || loading) return;

    runSimulation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized]);

  const debouncedSimulation = useRef(
    debounce((valor: number, vencimento: string) => {
      runSimulation({ valor, vencimento }, true);
    }, RECALC_DELAY),
  ).current;

  useEffect(() => {
    return () => debouncedSimulation.cancel();
  }, [debouncedSimulation]);

  const limits = simulation?.valorLimite;

  const amountValue = useMemo(
    () => Number(amountInput.replace(/\D/g, "")) / 100,
    [amountInput],
  );

  const amountError = useMemo(() => {
    if (!limits || !amountValue) return null;

    if (amountValue > limits.valorMaximoSolicitado) {
      return `O valor máximo para este cliente é ${formatCurrency(
        limits.valorMaximoSolicitado,
      )}`;
    }

    return null;
  }, [amountValue, limits]);

  const handleAmountChange = (value: string) => {
    const digits = value.replace(/\D/g, "");

    setAmountInput(digits ? formatAmount(Number(digits) / 100) : "");

    const amount = Number(digits) / 100;

    if (!amount || !limits || amount > limits.valorMaximoSolicitado) {
      debouncedSimulation.cancel();
      return;
    }

    debouncedSimulation(amount, dueDate);
  };

  const handleConfirmOffer = async () => {
    const offer = simulation?.prazoValor?.[selectedIndex];

    if (!preTransactionId || !simulation || !offer) return;

    try {
      setIsConfirming(true);

      await crefazCltService.putSelectProduct(
        preTransactionId,
        simulation.produtoId,
        {
          prazo: offer.prazo,
          valorParcela: offer.valorParcela,
          vencimento: simulation.vencimento,
          valorSolicitado: offer.valorSolicitado ?? simulation.valorSolicitado,
          tabelaJurosId: simulation.tabelaJurosId,
          tipoCalculo: simulation.tipoCalculo,
        },
      );

      setPreTransaction({
        id: preTransactionId,
        propostaId: simulation.propostaId,
      });

      setSelectedOffer({
        produtoId: simulation.produtoId,
        tabelaJurosId: simulation.tabelaJurosId,
        diaRecebimentoId: simulation.diaRecebimentoId,
        vencimento: simulation.vencimento,
        valorSolicitado: offer.valorSolicitado ?? simulation.valorSolicitado,
        prazo: offer.prazo,
        valorParcela: offer.valorParcela,
        commission: simulation.commission,
      });

      setStep(0);
      setIsProposalModalOpen(true);
    } catch (error: any) {
      toast({
        title: "Não foi possível confirmar a oferta",
        description: error?.message || "Tente novamente em alguns instantes.",
        variant: "destructive",
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const offers = useMemo(
    () => simulation?.prazoValor ?? ([] as PrazoValor[]),
    [simulation],
  );

  const sortedOffers = useMemo(
    () =>
      offers
        .map((offer, index) => ({ offer, index }))
        .sort((a, b) => a.offer.prazo - b.offer.prazo),
    [offers],
  );

  const sliderPosition = Math.max(
    sortedOffers.findIndex((item) => item.index === selectedIndex),
    0,
  );

  const handleSliderChange = (position: number) => {
    const item = sortedOffers[position];

    if (item) setSelectedIndex(item.index);
  };

  const selectedOffer = offers[selectedIndex] ?? null;

  const valorSolicitado =
    offers[selectedIndex]?.valorSolicitado ?? simulation?.valorSolicitado ?? 0;

  useRegisterSupportContext("simulation.crefazClt", {
    product: PartnerType.CREFAZ_CLT,
    step: "Simulação Crefaz CLT",
    data: {
      preTransactionId,
      authorized: isAuthorized,
      requestedValue: valorSolicitado,
      dueDate,
      selectedOffer,
    },
  });

  return {
    authorization,
    isAuthorized,
    simulation,
    loading,
    isRecalculating,
    error,
    amountInput,
    amountError,
    handleAmountChange,
    selectedIndex,
    setSelectedIndex,
    isConfirming,
    handleConfirmOffer,
    isProposalModalOpen,
    setIsProposalModalOpen,
    offers,
    sortedOffers,
    sliderPosition,
    handleSliderChange,
    selectedOffer,
    valorSolicitado,
    commission: simulation?.commission ?? 0,
  };
}

function formatAmount(value: number) {
  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatCurrency(value: number) {
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}
